import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { NumberParam, useQueryParam } from 'use-query-params';
import Skeleton from 'react-loading-skeleton';
import { useTheme } from 'styled-components';
import { BackArrow, Button, Input, Select, TopProgress } from '../../../../components';
import { BodyDefault, HeadLine1 } from '../../../../components/typography/styled';
import { billingService } from '../../../../api';
import { useUi } from '../../../../context/ui.context';
import { usePageTitle } from '../../../../context/navigationContext';
import { formatOrderTotal, getApiErrorMessage } from '../helpers';
import { Container, FormActions, FormCard, FormStack, HelpText, PanelTitle } from '../styles';

type OrderProductFormProps = {
  editMode?: boolean;
};

const getOptionInitialValue = (option: any) => {
  if (option.default_value) return option.default_value;
  if (option.widget === 'yesno') return 'no';
  return null;
};

const getChoicePriceLabel = (cycle: any) => {
  if (!cycle) return '';
  if (cycle.price_type === 'percentage') return `${cycle.price}%`;
  return formatOrderTotal(cycle.price, cycle.currency);
};

const getSelectedCycleId = (options: any, preferredCycle?: string | number | null) => {
  const cycles = options?.cycles || [];
  if (!cycles.length) return preferredCycle;

  const preferred = preferredCycle ?? options?.selected_cycle;
  const matchingCycle = cycles.find((item:any) => item.id?.toString() === preferred?.toString());

  return matchingCycle?.id ?? options?.selected_cycle ?? cycles[0]?.id;
};

const getPluginFieldName = (pluginLabel?: string) => {
  switch ((pluginLabel || '').toLowerCase()) {
    case 'cpanel':
      return 'cpanel_license_ip';
    case 'directadmin':
      return 'directadmin_license_ip';
    default:
      return undefined;
  }
};

const OrderProductForm: FC<OrderProductFormProps> = ({ editMode = false }) => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const theme:any = useTheme();
  const { viewAlert } = useUi();
  const [selectedClient] = useQueryParam('client', NumberParam);
  const [selectedUser] = useQueryParam('user', NumberParam);
  const { productCode, cycle, domainAction, domainName, cartItemId } = useParams<{
    productCode: string;
    cycle: string;
    domainAction: string;
    domainName: string;
    cartItemId: string;
  }>();

  const [orderOptions, setOrderOptions] = useState<any>();
  const [cartItem, setCartItem] = useState<any>();
  const [selectedCycle, setSelectedCycle] = useState<any>();
  const [configOptionValues, setConfigOptionValues] = useState<Record<string, any>>({});
  const [pluginData, setPluginData] = useState<Record<string, any>>({});
  const [formDomainAction, setFormDomainAction] = useState(domainAction || 'order_new');
  const [formDomainName, setFormDomainName] = useState(domainName || '');
  const [backendErrors, setBackendErrors] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  usePageTitle(orderOptions?.name ? `${t('ORDER')}: ${orderOptions.name}` : t('ORDER_PRODUCT'));

  const createFallbackPath = useMemo(() => {
    const query = new URLSearchParams();
    if (selectedClient) query.set('client', `${selectedClient}`);
    if (selectedUser) query.set('user', `${selectedUser}`);
    const queryString = query.toString();
    return `/orders/create${queryString ? `?${queryString}` : ''}`;
  }, [selectedClient, selectedUser]);

  const loadOptions = async (cycleId?: string | number) => {
    if (editMode) {
      if (!cartItemId) return;
      const [itemRes, optionsRes] = await Promise.all([
        billingService.getCartItem(cartItemId),
        billingService.getCartItemEditOptions(cartItemId),
      ]);
      const item = itemRes.data?.object || itemRes.data;
      const options = optionsRes.data?.object || optionsRes.data;
      setCartItem(item);
      setOrderOptions(options);
      setSelectedCycle(getSelectedCycleId(options, item.cycle));

      const values: Record<string, any> = {};
      options.configurable_options?.forEach((option:any) => {
        values[option.id] = getOptionInitialValue(option);
      });
      item.configurable_options?.forEach((option:any) => {
        values[option.option] = option.option_value;
      });
      setConfigOptionValues(values);
      setPluginData(item.plugin_data || {});
      return;
    }

    if (!productCode) return;
    const optionsRes = await billingService.getCartItemCreateOptions(productCode, cycleId || cycle);
    const options = optionsRes.data?.object || optionsRes.data;
    setOrderOptions(options);

    const nextCycle = getSelectedCycleId(options, cycleId || cycle);
    setSelectedCycle(nextCycle);

    const values: Record<string, any> = {};
    options.configurable_options?.forEach((option:any) => {
      values[option.id] = getOptionInitialValue(option);
    });
    setConfigOptionValues(values);
  };

  useEffect(() => {
    setLoading(true);
    loadOptions()
      .catch((error:any) => {
        viewAlert({
          severity: 'error',
          message: getApiErrorMessage(error, t('ERROR_OCCURRED')),
        });
        navigate(editMode ? '/orders/cart' : createFallbackPath);
      })
      .finally(() => setLoading(false));
  }, [productCode, cartItemId]);

  const cycleOptions = useMemo(() => {
    return (orderOptions?.cycles || []).map((item:any) => ({
      id: item.id,
      label: `${item.display_name || item.name || item.cycle} (${formatOrderTotal(item.fixed_price, item.currency)})`,
      value: item.id,
      options: item.setup_fee ? [`${t('SETUP_FEE')}: ${formatOrderTotal(item.setup_fee, item.currency)}`] : [],
    }));
  }, [orderOptions, t]);

  const productCycleChanged = (newCycle: any) => {
    setSelectedCycle(newCycle);
    const currentProductCode = orderOptions?.code || productCode;
    if (!currentProductCode) return;
    billingService.getCartItemCreateOptions(currentProductCode, newCycle)
      .then((res:any) => setOrderOptions(res.data?.object || res.data))
      .catch((error:any) => {
        viewAlert({
          severity: 'error',
          message: getApiErrorMessage(error, t('ERROR_OCCURRED')),
        });
      });
  };

  const setConfigOptionValue = (optionId: string | number, value: any) => {
    setConfigOptionValues((prev) => ({
      ...prev,
      [optionId]: value,
    }));
    setBackendErrors((prev) => {
      const next = {...prev};
      delete next[optionId];
      return next;
    });
  };

  const renderConfigurableOption = (option: any) => {
    const value = configOptionValues[option.id] ?? '';
    const title = option.display_name || option.display || option.name;
    const error = backendErrors[option.id];

    if (option.widget === 'drop' || option.widget === 'radio') {
      const choices = (option.choices || []).map((choice:any) => ({
        id: choice.choice,
        value: choice.choice,
        label: `${choice.label || choice.choice} (${getChoicePriceLabel(choice.cycle)})`,
      }));
      return (
        <div key={option.id}>
          <Select
            title={title}
            data={choices}
            selectedValue={value}
            onChange={(selected) => setConfigOptionValue(option.id, selected)}
            required={option.required}
          />
          {option.help_text ? <HelpText>{option.help_text}</HelpText> : null}
          {error ? <HelpText style={{color: '#E85048'}}>{error}</HelpText> : null}
        </div>
      );
    }

    if (option.widget === 'yesno') {
      const price = option.cycle?.price_type === 'percentage'
        ? `${option.cycle.price}%`
        : formatOrderTotal(option.cycle?.price, option.cycle?.currency);
      return (
        <div key={option.id}>
          <Select
            title={`${title} (${price})`}
            data={[
              { id: 'yes', value: 'yes', label: t('YES') },
              { id: 'no', value: 'no', label: t('NO') },
            ]}
            selectedValue={value || 'no'}
            onChange={(selected) => setConfigOptionValue(option.id, selected)}
            required={option.required}
          />
          {option.help_text ? <HelpText>{option.help_text}</HelpText> : null}
          {error ? <HelpText style={{color: '#E85048'}}>{error}</HelpText> : null}
        </div>
      );
    }

    return (
      <div key={option.id}>
        <Input
          title={title}
          placeholder={title}
          type={option.widget === 'num_in' ? 'number' : 'text'}
          value={value || ''}
          required={option.required}
          handleChange={(event:any) => setConfigOptionValue(option.id, event.target.value)}
        />
        {option.help_text ? <HelpText>{option.help_text}</HelpText> : null}
        {error ? <HelpText style={{color: '#E85048'}}>{error}</HelpText> : null}
      </div>
    );
  };

  const saveToCart = async () => {
    if (!orderOptions) return;
    const payloadPluginData = {...pluginData};
    delete payloadPluginData.valid;
    const pluginFieldName = getPluginFieldName(orderOptions.plugin_label);
    if (pluginFieldName && !payloadPluginData[pluginFieldName]) {
      setBackendErrors((prev) => ({...prev, [pluginFieldName]: t('REQUIRED_FIELD')}));
      return;
    }

    const configurableOptions = (orderOptions.configurable_options || [])
      .filter((option:any) => configOptionValues[option.id] !== null && configOptionValues[option.id] !== undefined && configOptionValues[option.id] !== '')
      .map((option:any) => ({
        option: option.id,
        option_value: configOptionValues[option.id],
      }));

    setSaving(true);
    setBackendErrors({});
    try {
      if (editMode && cartItemId) {
        await billingService.updateCartItem(cartItemId, {
          ...cartItem,
          cycle: selectedCycle,
          configurable_options: configurableOptions,
          plugin_data: payloadPluginData,
        });
        viewAlert({ severity: 'success', message: t('CART_ITEM_UPDATED') });
      } else {
        await billingService.createCartItem({
          product: orderOptions.id,
          cycle: selectedCycle,
          configurable_options: configurableOptions,
          domain_action: orderOptions.requires_domain ? formDomainAction : domainAction,
          domain_name: orderOptions.requires_domain ? formDomainName : domainName,
          plugin_data: payloadPluginData,
        });
        viewAlert({ severity: 'success', message: t('CART_ITEM_ADDED') });
      }
      navigate('/orders/cart');
    } catch (error:any) {
      const data = error?.response?.data;
      if (Array.isArray(data?.configurable_options) && data.configurable_options.length) {
        setBackendErrors(data.configurable_options[0]);
      } else if (data?.plugin_data) {
        setBackendErrors(data.plugin_data);
      }
      viewAlert({
        severity: 'error',
        message: getApiErrorMessage(error, t('PRODUCT_ORDER_FAILED')),
      });
    } finally {
      setSaving(false);
    }
  };

  const renderPluginFields = () => {
    const pluginFieldName = getPluginFieldName(orderOptions?.plugin_label);
    if (!pluginFieldName) return null;
    return (
      <Input
        title={t('IP_ADDRESS') as string}
        placeholder={t('IP_ADDRESS') as string}
        value={pluginData[pluginFieldName] || ''}
        required
        manualErr={backendErrors[pluginFieldName]}
        manualTouched={Boolean(backendErrors[pluginFieldName])}
        handleChange={(event:any) => {
          const value = event.target.value;
          setPluginData((prev) => ({...prev, [pluginFieldName]: value}));
          setBackendErrors((prev) => {
            const next = {...prev};
            delete next[pluginFieldName];
            return next;
          });
        }}
      />
    );
  };

  if (loading) {
    return (
      <Container>
        <TopProgress loading={loading} />
        <Skeleton
          baseColor={theme.palette[300]}
          highlightColor={theme.palette[20]}
          width={280}
          height={30}
          style={{marginBottom: '12px'}}
        />
        <Skeleton
          baseColor={theme.palette[300]}
          highlightColor={theme.palette[20]}
          width='100%'
          height={360}
        />
      </Container>
    );
  }

  if (!orderOptions) {
    return (
      <Container>
        <BodyDefault>{editMode ? t('CART_ITEM_NOT_FOUND') : t('PRODUCT_NOT_FOUND')}</BodyDefault>
      </Container>
    );
  }

  return (
    <Container>
      <BackArrow fallbackPath={editMode ? '/orders/cart' : createFallbackPath} />
      <FormCard>
        <HeadLine1>{orderOptions.name}</HeadLine1>
        {orderOptions.description ? <BodyDefault style={{whiteSpace: 'pre-wrap'}}>{orderOptions.description}</BodyDefault> : null}
        <FormStack style={{marginTop: '18px'}}>
          <Select
            title={t('PAYMENT_CYCLE_SELECTION') as string}
            data={cycleOptions}
            selectedValue={selectedCycle}
            onChange={productCycleChanged}
            required
          />
          {orderOptions.requires_domain ? (
            <>
              <PanelTitle>{t('DOMAIN_OPTIONS')}</PanelTitle>
              <Select
                title={t('DOMAIN_ACTION') as string}
                data={[
                  { id: 'order_new', value: 'order_new', label: t('ORDER_NEW_DOMAIN') },
                  { id: 'transfer', value: 'transfer', label: t('TRANSFER_DOMAIN') },
                  { id: 'use_external', value: 'use_external', label: t('USE_EXTERNAL_DOMAIN') },
                ]}
                selectedValue={formDomainAction}
                onChange={setFormDomainAction}
                required
              />
              <Input
                title={t('DOMAIN_NAME') as string}
                placeholder={t('DOMAIN_NAME') as string}
                value={formDomainName}
                required
                handleChange={(event:any) => setFormDomainName(event.target.value)}
              />
            </>
          ) : null}
          {orderOptions.configurable_options?.length ? (
            <>
              <PanelTitle>{t('CONFIGURABLE_OPTIONS')}</PanelTitle>
              {orderOptions.configurable_options.map((option:any) => renderConfigurableOption(option))}
            </>
          ) : null}
          {renderPluginFields()}
        </FormStack>
        <FormActions>
          <Button
            title={editMode ? t('SAVE') : t('SAVE_TO_CART')}
            variant='primary'
            size='display'
            loading={saving}
            onClick={saveToCart}
          />
          <Button
            title={t('CANCEL')}
            variant='stroke'
            size='display'
            disabled={saving}
            onClick={() => navigate(editMode ? '/orders/cart' : createFallbackPath)}
          />
        </FormActions>
      </FormCard>
    </Container>
  );
};

export default OrderProductForm;
